'use client';

import { useCouponPreviewCoupon } from '@ps/features/coupons/contexts/preview/context';
import { couponPreviewPrice } from '@ps/features/coupons/functions/couponPreviewPrice';
import type { Listing, ListingVariant } from '@ps/models/lib/listing';
import { cn } from '@ps/ui/utils';
import currency from 'currency.js';

import { getListingPriceInfo } from '../functions/listingDisplay';

type ProductPriceProps = {
  listing: Pick<
    Listing,
    | 'price'
    | 'discountPrice'
    | 'discountEnabled'
    | 'discountStartDate'
    | 'discountEndDate'
  >;
  /** Selected variant — when set, the price reflects its price/discount. */
  variant?: Pick<ListingVariant, 'price' | 'discountPrice'> | null;
  /** Render "Free Entry" instead of "FREE" for ticket / meeting listings. */
  isTicket?: boolean;
  className?: string;
};

/**
 * Mirrors RN `<PriceCell>` — single-row price with merchant-discount
 * strike-through, plus the display-only coupon preview (`?coupon=`, read from
 * `CouponPreviewProvider` — null outside it). When a coupon discount applies it
 * becomes the shown price and the pre-coupon price is struck through,
 * superseding the merchant-discount strike (single "was X / now Y").
 *
 * Reads the coupon context so EVERY price cell (physical row, digital header /
 * summary, sticky header) shows the discount uniformly — provider-safe, so it's
 * a no-op where no `CouponPreviewProvider` is mounted.
 */
export function ProductPrice({
  listing,
  variant,
  isTicket,
  className,
}: ProductPriceProps) {
  const priceInfo = getListingPriceInfo(listing, variant);

  const coupon = useCouponPreviewCoupon();
  const couponDiscountedPrice = couponPreviewPrice(
    { listPrice: priceInfo.price, currentPrice: priceInfo.effectivePrice },
    coupon,
  );

  const hasCouponDiscount =
    couponDiscountedPrice != null &&
    !priceInfo.isFree &&
    couponDiscountedPrice < priceInfo.effectivePrice;

  const displayedText = priceInfo.isFree
    ? isTicket
      ? 'Free Entry'
      : 'FREE'
    : currency(
        hasCouponDiscount ? couponDiscountedPrice : priceInfo.effectivePrice,
      ).format();

  // A coupon discount strikes the (pre-coupon) effective price; otherwise the
  // merchant-discount strikes the full price.
  const strikeText = hasCouponDiscount
    ? currency(priceInfo.effectivePrice).format()
    : priceInfo.isDiscountActive
      ? currency(priceInfo.price).format()
      : null;

  return (
    <div className={cn('text-body flex items-baseline gap-1.5', className)}>
      <span className="font-light tracking-tight text-black">
        {displayedText}
      </span>
      {strikeText ? (
        <span className="font-light text-[#9b9b9b] line-through">
          {strikeText}
        </span>
      ) : null}
    </div>
  );
}
